import { createContext, useContext, type ReactNode } from 'react';
import type { ConfirmTone } from './ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
}

/** Imperative confirm — resolves true if the user confirms, false otherwise. */
export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

export const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Access the branded confirmation dialog as a promise:
 *
 * ```ts
 * const confirm = useConfirm();
 * if (!(await confirm({ title, message, tone: 'danger' }))) return;
 * ```
 *
 * A drop-in, design-consistent replacement for `window.confirm`. Requires
 * `<ConfirmProvider>` above in the tree (mounted app-wide in providers.tsx).
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}
